import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trophy, Trash2, Download, UserPlus, Sparkles, Pencil } from "lucide-react";
import { toast } from "sonner";
import InviteAthletesDialog from "@/components/InviteAthletesDialog";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { CompetitionEditDialog } from "@/components/CompetitionEditDialog";
import { DELETE_COMP_MSG } from "@/pages/Competitions";
import { AthleteSelect } from "@/components/AthleteSelect";
import { displayName, initials, sortAthletes, matchesSearch } from "@/lib/athleteName";

const ROUNDS = ["T256", "T128", "T64", "T32", "T16", "T8", "SF", "Finale"];
const EVAL_OPTIONS = [
  { v: "sotto_attese", l: "Sotto le attese", c: "bg-red-50 text-red-700" },
  { v: "attese", l: "Come da attese", c: "bg-zinc-100 text-zinc-700" },
  { v: "sopra_attese", l: "Sopra le attese", c: "bg-emerald-50 text-emerald-700" },
];

export default function CompetitionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [comp, setComp] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const deleteComp = async () => {
    try { await api.delete(`/competitions/${id}`); toast.success("Gara eliminata"); nav("/gare"); }
    catch { toast.error("Errore durante l'eliminazione"); }
  };
  const [athletes, setAthletes] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newAthleteId, setNewAthleteId] = useState("");
  const [editingIdx, setEditingIdx] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);

  const load = async () => {
    const [c, a] = await Promise.all([api.get(`/competitions/${id}`), api.get("/athletes?include_archived=1")]);
    setComp({ ...c.data, invited_athlete_ids: c.data.invited_athlete_ids || [] });
    setAthletes(a.data);
  };
  useEffect(() => { load(); }, [id]);

  const downloadTravelPack = () => {
    const token = localStorage.getItem("csp_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/competitions/${id}/travel-pack`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => { if (!r.ok) throw new Error("err"); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `trasferta_${comp?.name || "gara"}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Pacchetto trasferta scaricato");
      })
      .catch(() => toast.error("Errore export"));
  };

  const save = async () => {
    await api.put(`/competitions/${id}`, comp);
    toast.success("Salvato");
  };

  const addAthlete = async () => {
    if (!newAthleteId) return;
    if (comp.results?.some((r) => r.athlete_id === newAthleteId)) return toast.error("Atleta già iscritto");
    const updated = { ...comp, results: [...(comp.results || []), { athlete_id: newAthleteId, pool_wins: 0, pool_losses: 0, hits_scored: 0, hits_received: 0, indicator: 0, de_matches: [] }] };
    setComp(updated);
    await api.put(`/competitions/${id}`, updated);
    setAddOpen(false);
    setNewAthleteId("");
    toast.success("Atleta aggiunto");
  };

  const updateResult = (idx, patch) => {
    const results = [...comp.results];
    results[idx] = { ...results[idx], ...patch };
    results[idx].indicator = (results[idx].hits_scored || 0) - (results[idx].hits_received || 0);
    setComp({ ...comp, results });
  };

  const removeResult = (idx) => {
    const results = comp.results.filter((_, i) => i !== idx);
    setComp({ ...comp, results });
  };

  const addDE = (idx) => {
    const results = [...comp.results];
    results[idx] = { ...results[idx], de_matches: [...(results[idx].de_matches || []), { round: "T32", opponent: "", score_for: 0, score_against: 0, won: false }] };
    setComp({ ...comp, results });
  };

  const updateDE = (idx, dIdx, patch) => {
    const results = [...comp.results];
    const de = [...results[idx].de_matches];
    de[dIdx] = { ...de[dIdx], ...patch };
    de[dIdx].won = de[dIdx].score_for > de[dIdx].score_against;
    results[idx] = { ...results[idx], de_matches: de };
    setComp({ ...comp, results });
  };

  const removeDE = (idx, dIdx) => {
    const results = [...comp.results];
    results[idx] = { ...results[idx], de_matches: results[idx].de_matches.filter((_, i) => i !== dIdx) };
    setComp({ ...comp, results });
  };

  if (!comp) return <div className="text-zinc-500">Caricamento…</div>;

  const availableAthletes = athletes.filter((a) => !comp.results?.some((r) => r.athlete_id === a.id));

  return (
    <div className="space-y-6">
      <Link to="/gare" className="text-sm text-zinc-500 flex items-center gap-1 hover:text-black"><ArrowLeft className="w-4 h-4" />Torna alle gare</Link>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider mb-2"><Trophy className="w-4 h-4" /><span className="font-mono">{comp.date}</span></div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">{comp.name}</h1>
          <div className="text-sm text-zinc-500 font-mono mt-1">{comp.location || "—"} · {comp.weapon} · {comp.category || "—"}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <InviteAthletesDialog
            competition={comp}
            onDone={load}
            trigger={<Button data-testid="invite-athletes-btn" variant="outline" className="h-11"><UserPlus className="w-4 h-4 mr-2" />Convoca atleti</Button>}
          />
          <Button data-testid="travel-pack-btn" variant="outline" className="h-11" onClick={downloadTravelPack}><Download className="w-4 h-4 mr-2" />Pacchetto trasferta</Button>
          <Button data-testid="edit-comp-btn" variant="outline" className="h-11" onClick={() => setEditOpen(true)}><Pencil className="w-4 h-4 mr-2" />Modifica gara</Button>
          <CompetitionEditDialog competition={comp} open={editOpen} onOpenChange={setEditOpen} onSaved={load} />
          <Button data-testid="delete-comp-btn" variant="outline" className="h-11 text-zinc-500 hover:text-red-600 hover:border-red-300" onClick={() => setDelOpen(true)}><Trash2 className="w-4 h-4 mr-2" />Elimina</Button>
          <ConfirmDeleteDialog open={delOpen} onOpenChange={setDelOpen} title="Eliminare la gara?" description={DELETE_COMP_MSG} onConfirm={deleteComp} testId="delete-comp" />
          <Button data-testid="ai-analyze-btn" variant="outline" className="h-11" onClick={async () => {
            setAiLoading(true);
            try { const { data } = await api.post(`/ai/analysis/competition/${id}`); setAiAnalysis(data); toast.success("Analisi AI generata"); }
            catch (e) { toast.error(e.response?.data?.detail || "Errore AI"); }
            finally { setAiLoading(false); }
          }} disabled={aiLoading}><Sparkles className="w-4 h-4 mr-2" />{aiLoading ? "Analisi…" : "Analisi AI"}</Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-comp-athlete" variant="outline" className="h-11"><Plus className="w-4 h-4 mr-2" />Iscrivi atleta</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Iscrivi atleta</DialogTitle></DialogHeader>
              <div className="py-3">
                <AthleteSelect
                  value={newAthleteId}
                  onChange={setNewAthleteId}
                  athletes={availableAthletes}
                  testid="comp-add-athlete"
                  placeholder="Seleziona atleta da iscrivere"
                />
              </div>
              <DialogFooter><Button data-testid="add-comp-athlete-save" onClick={addAthlete} className="bg-black hover:bg-zinc-800">Aggiungi</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Button data-testid="save-comp" onClick={save} className="h-11 bg-black hover:bg-zinc-800">Salva</Button>
        </div>
      </div>

      {aiAnalysis && (
        <div className="bg-white border border-zinc-200 rounded-md p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-[#0052FF]" />
            <h2 className="font-heading font-semibold">Analisi AI</h2>
            <span className="text-xs text-zinc-500 font-mono">{aiAnalysis.model}</span>
          </div>
          <div className="whitespace-pre-wrap text-sm text-zinc-800 bg-zinc-50 border border-zinc-200 rounded p-4">{aiAnalysis.content}</div>
        </div>
      )}

      <div className="space-y-4">
        {(comp.results || []).map((r, idx) => {
          const ath = athletes.find((a) => a.id === r.athlete_id);
          const expanded = editingIdx === idx;
          return (
            <div key={r.athlete_id} className="bg-white border border-zinc-200 rounded-md">
              <button onClick={() => setEditingIdx(expanded ? null : idx)} data-testid={`comp-result-${r.athlete_id}`} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-zinc-50">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center font-semibold text-sm">
                    {initials(ath)}
                  </div>
                  <div>
                    <div className="font-medium">{displayName(ath)}</div>
                    <div className="text-xs text-zinc-500 font-mono">
                      {r.ranking ? `#${r.ranking}` : "—"} / {r.participants || "—"} · Pool {r.pool_wins}-{r.pool_losses} · Ind {r.indicator > 0 ? "+" : ""}{r.indicator}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {r.evaluation && <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${EVAL_OPTIONS.find((e) => e.v === r.evaluation)?.c || ""}`}>{EVAL_OPTIONS.find((e) => e.v === r.evaluation)?.l}</span>}
                  <button onClick={(e) => { e.stopPropagation(); removeResult(idx); }} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </button>

              {expanded && (
                <div className="border-t border-zinc-200 p-5 space-y-4">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div><Label>Classifica</Label><Input type="number" value={r.ranking || ""} onChange={(e) => updateResult(idx, { ranking: parseInt(e.target.value || 0) || null })} /></div>
                    <div><Label>Partecipanti</Label><Input type="number" value={r.participants || ""} onChange={(e) => updateResult(idx, { participants: parseInt(e.target.value || 0) || null })} /></div>
                    <div><Label>V pool</Label><Input type="number" value={r.pool_wins || 0} onChange={(e) => updateResult(idx, { pool_wins: parseInt(e.target.value || 0) })} /></div>
                    <div><Label>D pool</Label><Input type="number" value={r.pool_losses || 0} onChange={(e) => updateResult(idx, { pool_losses: parseInt(e.target.value || 0) })} /></div>
                    <div><Label>H dati</Label><Input type="number" value={r.hits_scored || 0} onChange={(e) => updateResult(idx, { hits_scored: parseInt(e.target.value || 0) })} /></div>
                    <div><Label>H subiti</Label><Input type="number" value={r.hits_received || 0} onChange={(e) => updateResult(idx, { hits_received: parseInt(e.target.value || 0) })} /></div>
                    <div className="col-span-2"><Label>Indicatore</Label><div className="font-mono text-2xl h-10 flex items-center">{r.indicator > 0 ? "+" : ""}{r.indicator}</div></div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Diretta eliminazione</Label>
                      <Button data-testid={`add-de-${idx}`} size="sm" variant="outline" onClick={() => addDE(idx)}><Plus className="w-3 h-3 mr-1" />Match</Button>
                    </div>
                    <div className="space-y-2">
                      {(r.de_matches || []).map((d, di) => (
                        <div key={di} className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-2">
                            <Select value={d.round} onValueChange={(v) => updateDE(idx, di, { round: v })}>
                              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                              <SelectContent>{ROUNDS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-5"><Input placeholder="Avversario" value={d.opponent} onChange={(e) => updateDE(idx, di, { opponent: e.target.value })} /></div>
                          <div className="col-span-2"><Input type="number" value={d.score_for} onChange={(e) => updateDE(idx, di, { score_for: parseInt(e.target.value || 0) })} /></div>
                          <div className="col-span-2"><Input type="number" value={d.score_against} onChange={(e) => updateDE(idx, di, { score_against: parseInt(e.target.value || 0) })} /></div>
                          <div className="col-span-1 flex items-center gap-1">
                            <span className={`text-xs font-mono font-bold ${d.won ? "text-emerald-600" : "text-red-600"}`}>{d.won ? "V" : "D"}</span>
                            <button onClick={() => removeDE(idx, di)} className="text-zinc-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ))}
                      {(r.de_matches || []).length === 0 && <div className="text-xs text-zinc-500">Nessun match di DE registrato.</div>}
                    </div>
                  </div>

                  <div>
                    <Label>Valutazione</Label>
                    <div className="flex gap-2 mt-2">
                      {EVAL_OPTIONS.map((e) => (
                        <button
                          key={e.v}
                          data-testid={`eval-${idx}-${e.v}`}
                          onClick={() => updateResult(idx, { evaluation: e.v })}
                          className={`px-4 py-2 rounded-md text-sm border ${r.evaluation === e.v ? "bg-black text-white border-black" : "bg-white border-zinc-200"}`}
                        >{e.l}</button>
                      ))}
                    </div>
                  </div>

                  <div className="grid lg:grid-cols-3 gap-3">
                    <div><Label>Punto di forza</Label><Textarea rows={2} value={r.strength || ""} onChange={(e) => updateResult(idx, { strength: e.target.value })} /></div>
                    <div><Label>Punto debole</Label><Textarea rows={2} value={r.weakness || ""} onChange={(e) => updateResult(idx, { weakness: e.target.value })} /></div>
                    <div><Label>Prossimo obiettivo</Label><Textarea rows={2} value={r.next_objective || ""} onChange={(e) => updateResult(idx, { next_objective: e.target.value })} /></div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {(comp.results || []).length === 0 && <div className="text-zinc-500 text-center py-10">Nessun atleta iscritto. Aggiungi con "Iscrivi atleta".</div>}
      </div>
    </div>
  );
}
